import { useCallback, useState } from 'react';
import { Upload, File, X, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

const FileUpload = ({ onFileSelect, selectedFile, onClear, error }) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        onFileSelect(file);
      }
    }
  }, [onFileSelect]);

  const handleFileInput = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onFileSelect(files[0]);
    }
  }, [onFileSelect]);

  return (
    <div className="w-full" data-testid="file-upload">
      {!selectedFile ? (
        <div
          className={`
            border-2 border-dashed rounded-xl p-8 text-center
            transition-all duration-200 cursor-pointer
            ${isDragOver 
              ? 'border-accent bg-accent/5' 
              : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
            }
            ${error ? 'border-red-300 bg-red-50/50' : ''}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input').click()}
        >
          <input
            id="file-input"
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={handleFileInput}
            data-testid="file-input"
          />
          
          <div className="flex flex-col items-center gap-4">
            <div className={`
              w-16 h-16 rounded-full flex items-center justify-center
              ${isDragOver ? 'bg-accent/10' : 'bg-slate-100'}
            `}>
              <Upload className={`h-8 w-8 ${isDragOver ? 'text-accent' : 'text-slate-400'}`} />
            </div>
            
            <div>
              <p className="text-slate-900 font-medium mb-1">
                Drop your resume here or click to browse
              </p>
              <p className="text-sm text-slate-500">
                PDF files only, max 5MB
              </p>
            </div>
            
            <Button 
              variant="outline" 
              className="mt-2"
              type="button"
              data-testid="browse-button"
            >
              Browse Files
            </Button>
          </div>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl p-4 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                <File className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <p className="font-medium text-slate-900" data-testid="selected-filename">
                  {selectedFile.name}
                </p>
                <p className="text-sm text-slate-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClear}
              className="text-slate-400 hover:text-slate-600"
              data-testid="clear-file-button"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
      )}
      
      {error && (
        <div className="mt-3 flex items-center gap-2 text-red-600 text-sm" data-testid="upload-error">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
